import { useState } from 'react'
import { Alert, Badge, Button, Tabs, Text } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { WithId } from '@medplum/core'
import { DocumentReference, Patient, Practitioner, Reference } from '@medplum/fhirtypes'

import { FaxTriagePanel } from './FaxTriagePanel'
import { FaxSplitPanel } from './FaxSplitPanel'
import { FaxActionsTab } from './FaxActionsTab'
import { useFaxTaskSummaries } from '@/hooks/useFaxTasks'
import type { OcrData } from '@/hooks/useFaxInbox'
import type { FaxDocumentType } from '@/hooks/useFaxTriage'
import type { FaxPatients } from '@/hooks/useFaxPatients'

export function FaxPanel({
  documentReference,
  taskId,
  disabled = false,
  defaultPatient,
  defaultTitle,
  defaultReferringProvider,
  defaultLabName,
  defaultDocumentType,
  ocrData,
  faxPatients,
  onViewPage,
  onDelete,
  deleteLoading,
}: {
  documentReference: WithId<DocumentReference>
  taskId: string
  disabled?: boolean
  defaultPatient?: Reference<Patient>
  defaultTitle?: string
  defaultReferringProvider?: Reference<Practitioner>
  defaultLabName?: string
  defaultDocumentType?: FaxDocumentType
  ocrData: OcrData
  // Who is on each page (PD-1988); undefined while loading or after a failed read.
  faxPatients?: FaxPatients
  // Jumps the viewer to a page from a split row.
  onViewPage: (page: number) => void
  onDelete: () => void
  deleteLoading: boolean
}) {
  const { byDocumentReference } = useFaxTaskSummaries([documentReference.id])
  const summary = byDocumentReference.get(documentReference.id)
  const openCount = summary?.tasks.filter((t) => !t.done).length ?? 0
  // Staff's explicit choice between the forms; undefined until they make one.
  const [fileAsOne, setFileAsOne] = useState<boolean>()
  const [singleDirty, setSingleDirty] = useState(false)

  const groups = faxPatients?.groups ?? []
  const splitInProgress = faxPatients?.split?.state === 'in-progress'
  const multiPatient = !disabled && groups.length >= 2
  // OCR can miss a patient: an open fax of several pages can still be split by hand.
  const canSplitByHand =
    !disabled &&
    !faxPatients?.split &&
    groups.length < 2 &&
    faxPatients?.status !== 'pending' &&
    (faxPatients?.pageCount ?? 0) >= 2
  // A split in flight or done always shows. Otherwise several patients bring up the split form,
  // unless staff chose one document or are already typing into the single form (the patient read
  // can land mid-entry, and swapping forms then would throw their input away).
  const showSplit =
    !!faxPatients?.split ||
    (fileAsOne === false && (multiPatient || canSplitByHand)) ||
    (multiPatient && fileAsOne === undefined && !singleDirty)
  // The single form uses up a suggestion even while disabled, so offer one only to an open fax
  // with a single patient on it.
  const suggestion = !disabled && groups.length === 1 ? groups[0].suggestedPatient : null
  const suggestedPatient: Reference<Patient> | undefined = suggestion
    ? { reference: `Patient/${suggestion.id}`, display: suggestion.display }
    : undefined

  return (
    <div className="flex flex-col gap-4">
      <Tabs defaultValue="triage">
        <Tabs.List>
          <Tabs.Tab value="triage">Triage</Tabs.Tab>
          <Tabs.Tab
            value="actions"
            rightSection={
              openCount > 0 ? (
                <Badge color={summary?.state === 'overdue' ? 'red' : 'yellow'} size="xs" circle>
                  {openCount}
                </Badge>
              ) : undefined
            }
          >
            Actions
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="triage" pt="sm">
          {showSplit && faxPatients ? (
            <FaxSplitPanel
              taskId={taskId}
              faxPatients={faxPatients}
              onViewPage={onViewPage}
              onFileAsOne={() => setFileAsOne(true)}
              defaultLabName={defaultLabName ?? ocrData.labName ?? undefined}
            />
          ) : (
            <>
              {multiPatient && (
                <Alert color="yellow" p="sm" mb="sm">
                  <Text size="sm" fw={600}>
                    {groups.length} patients found in this fax.
                  </Text>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    mt="xs"
                    ml={-8}
                    onClick={() => setFileAsOne(false)}
                  >
                    Split by patient
                  </Button>
                </Alert>
              )}
              {canSplitByHand && (
                <div className="mb-2 flex items-center gap-1">
                  <Text size="xs" c="dimmed">
                    More than one patient on this fax?
                  </Text>
                  <Button variant="subtle" size="compact-xs" onClick={() => setFileAsOne(false)}>
                    Split by patient
                  </Button>
                </div>
              )}
              <FaxTriagePanel
                documentReference={documentReference}
                taskId={taskId}
                disabled={disabled}
                defaultPatient={defaultPatient}
                suggestedPatient={suggestedPatient}
                defaultTitle={defaultTitle}
                defaultReferringProvider={defaultReferringProvider}
                defaultLabName={defaultLabName}
                defaultDocumentType={defaultDocumentType}
                ocrData={ocrData}
                checkPatients={(faxPatients?.pages.length ?? 0) > 0}
                onDirtyChange={setSingleDirty}
              />
            </>
          )}

          {/* Deliberately inside the Triage panel only: deleting the whole fax is
              not one of the follow-up actions, and offering it beneath the task
              list invites a destructive misclick. */}
          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            loading={deleteLoading}
            // Deleting removes the fax Task, which holds an unfinished split's plan.
            disabled={splitInProgress}
            onClick={onDelete}
            mt="md"
            fullWidth
          >
            Delete fax
          </Button>
          {splitInProgress && (
            <Text size="xs" c="dimmed" mt={4}>
              Finish filing before deleting this fax.
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="actions" pt="sm">
          <FaxActionsTab
            documentReferenceId={documentReference.id}
            defaultPatient={defaultPatient}
            faxTaskId={taskId}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}
